import { resolveBoundColumnPair } from '@inference.engine/taxonomy/web';
import type { TaxonomyListColumn } from '../model/types';

export interface ListColumns {
  value: TaxonomyListColumn | undefined;
  code: TaxonomyListColumn | undefined;
  /** Every other stored column, in stored order. Edited one cell at a time; a block paste and fill skip them. */
  extras: TaxonomyListColumn[];
}

// Tolerates a column with no name, as the shared pair rule does, rather than throwing.
function isNamedCode(column: TaxonomyListColumn) {
  return ((column.name as string | undefined) ?? '').trim().toLowerCase() === 'code';
}

/**
 * The two columns the editor writes, picked by the same pair rule a new field binding starts
 * from, so the column labelled Code here is the code column that rule picks.
 */
export function listColumns(columns: TaxonomyListColumn[]): ListColumns {
  const pair = resolveBoundColumnPair(columns);
  if (pair) {
    return {
      value: pair.valueColumn,
      code: pair.codeColumn,
      extras: columns.filter((c) => c !== pair.valueColumn && c !== pair.codeColumn),
    };
  }
  // One column or none. Matches what the pair rule picks once `withValueAndCode` has added the
  // missing one, so the first save does not swap which column is which.
  const [only] = columns as (TaxonomyListColumn | undefined)[];
  if (only && isNamedCode(only)) return { value: undefined, code: only, extras: [] };
  return { value: only, code: undefined, extras: [] };
}

/**
 * The columns with a Value and a Code column appended where missing, so a legacy one-column list
 * gains the other one with the first save. The stored names stay English: a binding finds its
 * column by id, and the default pair rule matches `Code` by name. A column with no name gets an
 * empty one, which the API accepts where a missing or null name is refused.
 */
export function withValueAndCode(
  columns: TaxonomyListColumn[],
  newId: () => string = () => crypto.randomUUID(),
): TaxonomyListColumn[] {
  const { value, code } = listColumns(columns);
  return [
    ...columns.map((c) => ((c.name as string | null | undefined) == null ? { ...c, name: '' } : c)),
    // eslint-disable-next-line i18next/no-literal-string -- persisted column name, not copy
    ...(value ? [] : [{ id: newId(), name: 'Value' }]),
    // eslint-disable-next-line i18next/no-literal-string -- persisted column name, not copy
    ...(code ? [] : [{ id: newId(), name: 'Code' }]),
  ];
}

/**
 * The columns a binding picker offers: Value and Code, plus any other column a binding already
 * reads, so that binding still shows what it is set to. Stored order.
 */
export function bindableColumns(
  columns: TaxonomyListColumn[],
  boundColumnIds: (string | null | undefined)[],
): TaxonomyListColumn[] {
  const { value, code } = listColumns(columns);
  return columns.filter((c) => c === value || c === code || boundColumnIds.includes(c.id));
}
